import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DrawIcon from "@mui/icons-material/Draw";
import EditOffIcon from "@mui/icons-material/EditOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import useMediaQuery from "@mui/material/useMediaQuery";
import { touchTargetSx } from "_shared/_views/touch-target";
import { SESSION_RECORDING_MASK_CLASS } from "_shared/session-recording/session-recording-privacy";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useOwnIndividual } from "marketplace/individuals/own-individual";
import { type PermissionKind, useGetPermissionKindsQuery } from "permissions/_api/permission-kinds-api-service";
import ApplyTemplateDialog from "permissions/_views/dialogs/apply-template-dialog";
import { GrantExportButton, useGrantExport } from "permissions/_views/export/export-grants-button";
import type { PersonExportStates } from "permissions/_views/export/grant-export-reads";
import GrantRow from "permissions/_views/grant-row/grant-row";
import { buildGrantRow, grantPeopleIds, type GrantRowModel } from "permissions/_views/grant-row/grant-row-model";
import AddAccessDialog from "permissions/_views/people/add-access-dialog";
import PersonAreaSection, { areaAnchorId } from "permissions/_views/people/person-area-section";
import PersonGrantDetails, { type GrantDetailsSelection } from "permissions/_views/people/person-grant-details";
import usePersonKindReach from "permissions/_views/people/person-kind-reach";
import usePersonMemberInvitationAnswer from "permissions/_views/people/person-member-invitation-answer";
import {
  PERSON_GRANT_FILTERS,
  type PersonGrantFilter,
  type PersonKindRow,
  areaCounts,
  buildPersonAreas,
  filterPersonAreas,
  holdsSigningAuthority,
  liveSigningAuthorityGrants,
  personSummary,
  removeAllAccessReview,
  signingAuthorityKind,
  withRegranted,
} from "permissions/_views/people/person-grants-model";
import {
  OWN_PERMISSIONS_SEGMENT,
  RECORDS_PAGE_PARAM,
  RECORDS_PARAM,
  RECORDS_QUERY_PARAM,
  RECORDS_TARGET_PARAM,
} from "permissions/_views/people/person-permissions-path";
import { targetIdOf } from "permissions/_views/people/person-record-grants-model";
import PersonRecordList from "permissions/_views/people/person-record-list";
import RemoveAllAccessDialog from "permissions/_views/people/remove-all-access-dialog";
import usePersonGrantChanges, { REMOVE_ALL_KEY } from "permissions/_views/people/use-person-grant-changes";
import usePersonGrants from "permissions/_views/people/use-person-grants";
import { permissionAreaToken } from "permissions/_views/permission-areas";
import { personNameState, removerNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { type NameState, personStateText } from "permissions/_views/targets/permission-target-state";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import ApplyTemplateMenuButton from "permissions/_views/templates/apply-template-menu-button";
import {
  ALSO_GRANTED,
  REMOVED_JUST_NOW,
  changeAnnouncements,
  accessibleNames,
  memberRole,
  removeAllAccess,
  unnamedPerson,
} from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import useSessionUser from "permissions/session/use-session-user";
import type PermissionTemplate from "permissions/templates/permission-template";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";

/**
 * Everything one person holds in the viewer's organization, at `/permissions/people/{userId}`
 * (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The person page").
 *
 * The standing kinds are listed in full, area by area in the server's order: what the person holds,
 * then what they do not, folded. Every row is a `GrantRow`. The header names the person and the
 * organization, counts what they hold, and carries the page's actions: **Make a signer** (or
 * **Remove signing authority**), **Add access**, **Apply template**, and a menu, whose **Remove all
 * access…** opens a review step before the grantee-scoped disable (#6176).
 *
 * `/permissions/people/me` is the viewer's own page, open to every member and read-only: nobody may
 * change their own permissions, and the server refuses it.
 */

export const OWN_PAGE_NOTICE = "You cannot change your own permissions. Ask another administrator.";

/**
 * Which grants the export holds under each state filter: Removed exports the removed ones, Granted
 * the live ones, and All every grant, removed included. Not granted lists kinds the person has no
 * grant of, which no line of a file of grants can say, so it exports every grant as All does.
 */
export const EXPORT_STATES: Readonly<Record<PersonGrantFilter, PersonExportStates>> = {
  all: "both",
  granted: "live",
  "not-granted": "both",
  removed: "removed",
};

const CONTENT_MAX = 1100;

const Page = styled(Box)({
  height: "100%",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
});

/**
 * One column, capped at about 1100 px; a side nav of the areas beside it at lg; and at xl the width
 * to spare becomes a details pane for the row chosen with "Show details".
 */
const Layout = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `minmax(0, ${CONTENT_MAX}px)`,
  justifyContent: "center",
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: { padding: theme.spacing(2, 3) },
  [theme.breakpoints.up("lg")]: { gridTemplateColumns: `11rem minmax(0, ${CONTENT_MAX}px)` },
  [theme.breakpoints.up("xl")]: { gridTemplateColumns: `11rem minmax(0, ${CONTENT_MAX}px) 20rem` },
}));

const SideColumn = styled(Box)({ position: "sticky", top: 16, alignSelf: "start" });

/** Sticky at md and up, so the person and the filters stay in view; it scrolls away on a phone. */
const StickyHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  paddingBottom: theme.spacing(1.5),
  [theme.breakpoints.up("md")]: {
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
}));

/** The height of the sticky header, which the sticky area headers sit under. */
function useHeight(element: HTMLElement | null): number {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return height;
}

function AreaNav(props: Readonly<{ areas: readonly string[] }>) {
  return (
    <Box component="nav" aria-label="Areas">
      <Typography variant="overline" color="text.secondary" component="p">
        Areas
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {props.areas.map((area) => {
          const token = permissionAreaToken(area);
          return (
            <li key={area}>
              <Link
                href={`#${areaAnchorId(area)}`}
                underline="hover"
                color="text.primary"
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, minHeight: 32 }}
              >
                <token.Icon fontSize="small" sx={{ color: token.color }} aria-hidden />
                {area}
              </Link>
            </li>
          );
        })}
      </Box>
    </Box>
  );
}

function AreaSkeletons() {
  return (
    <Box aria-busy="true" aria-label="Loading permissions" sx={{ display: "grid", gap: 2 }}>
      {[0, 1, 2].map((index) => (
        <Box key={index} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, display: "grid", gap: 1.5 }}>
          <Skeleton variant="text" width="30%" />
          <Skeleton variant="rectangular" height={44} />
          <Skeleton variant="rectangular" height={44} />
        </Box>
      ))}
    </Box>
  );
}

type Person = Readonly<{
  userId: Guid;
  name?: string;
  email?: string;
  avatar: ReactNode;
  /** Whether the directory named the person, and if not, why. */
  state: NameState;
  /** Asks the directory again, when the lookup failed. */
  retry?: () => void;
}>;

/**
 * The page's title: the name, skeleton text while the directory is asked, or the person state
 * ("No longer a member") with Retry where the lookup failed.
 */
function personTitle(person: Person): Readonly<{ title: string; loading: boolean; action?: ReactNode }> {
  if (person.state.state === "found") return { title: person.state.name, loading: false };
  if (person.state.state === "loading") return { title: "", loading: true };
  const action =
    person.state.state === "failed" && person.retry ? (
      <Button size="small" onClick={person.retry} aria-label={accessibleNames.retryPerson}>
        Retry
      </Button>
    ) : undefined;
  return { title: personStateText(person.state) ?? "", loading: false, action };
}

function PersonPermissions(
  props: Readonly<{
    person: Person;
    isOwnPage: boolean;
    kinds: readonly PermissionKind[];
    areas: readonly string[];
  }>,
) {
  const { person, isOwnPage, kinds, areas } = props;
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isExtraLarge = useMediaQuery(theme.breakpoints.up("xl"));
  const { openDialog } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionUser = useSessionUser();
  const viewerUserId = sessionUser?.id ?? Guid.EMPTY;
  const mayAdministerPlatform = sessionUser?.canAdministerPlatform() ?? false;
  // Every write asks the server for Member Permissions at Read/Write; a reader who has only Read-Only
  // sees the page as it is and is offered nothing to press.
  const readOnly = isOwnPage || !(sessionUser?.canGrantSigningAuthority() ?? false);
  const organization = sessionUser?.companyName;
  // A person the directory did not name is "this person" in every sentence, never the state
  // sentence: "Filter No longer a member's grants" read the state as a name (#6429).
  const knownName = person.name || person.email || undefined;
  const personName = knownName ?? unnamedPerson.subject;

  const [filter, setFilter] = useState<PersonGrantFilter>("all");
  const [text, setText] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [removeAllOpen, setRemoveAllOpen] = useState(false);
  const removeAllAnchor = useRef<HTMLElement | null>(null);
  // A standing row's key, or a per-record grant as its list last drew it: the page holds no copy of
  // the per-record grants, so the list hands over what the pane shows.
  const [selected, setSelected] = useState<string | GrantDetailsSelection>();
  const [headerElement, setHeaderElement] = useState<HTMLDivElement | null>(null);
  const headerHeight = useHeight(headerElement);

  const personGrants = usePersonGrants(person.userId, kinds);
  const reachOf = usePersonKindReach(person.userId, {
    mayReadMemberPermissions: sessionUser?.canReadMemberPermissions() ?? false,
    mayAdministerPlatform,
  });
  const withInvitationAnswer = usePersonMemberInvitationAnswer(person.userId, sessionUser);
  const changes = usePersonGrantChanges(
    { userId: person.userId, name: personName, legalEntityId: sessionUser?.legalEntityId ?? Guid.EMPTY },
    viewerUserId,
  );

  // A grant granted again here is drawn until a list holds it (#6668). Memoized on purpose: every
  // section is built from it.
  const grants = useMemo(() => withRegranted(personGrants.grants, changes.regranted), [personGrants.grants, changes.regranted]);

  // The removed counts are what the Removed filter lists per-record kinds from, and what the header
  // counts them in; without them neither reaches a removed per-record grant (#6428).
  const context = useMemo(
    () => ({
      areas,
      kinds,
      grants,
      mayAdministerPlatform,
      permissionCounts: personGrants.permissionCounts,
      removedPermissionCounts: personGrants.removedPermissionCounts,
    }),
    [areas, kinds, grants, mayAdministerPlatform, personGrants.permissionCounts, personGrants.removedPermissionCounts],
  );

  // The open per-record list, its page, its search and the one record it is narrowed to are the query
  // string, so Back closes the list and a link opens it again where it was.
  const openRecords = searchParams.get(RECORDS_PARAM) ?? undefined;
  const recordsPage = Math.max(Number.parseInt(searchParams.get(RECORDS_PAGE_PARAM) ?? "1", 10) || 1, 1) - 1;
  const recordsQuery = searchParams.get(RECORDS_QUERY_PARAM) ?? "";
  const recordsTarget = searchParams.get(RECORDS_TARGET_PARAM) ?? undefined;
  const setRecordsState = useCallback(
    (slug: string | undefined, pageIndex: number, query: string, replace: boolean, targetId?: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(RECORDS_PARAM);
          next.delete(RECORDS_PAGE_PARAM);
          next.delete(RECORDS_QUERY_PARAM);
          next.delete(RECORDS_TARGET_PARAM);
          if (slug) {
            next.set(RECORDS_PARAM, slug);
            if (pageIndex > 0) next.set(RECORDS_PAGE_PARAM, String(pageIndex + 1));
            if (query) next.set(RECORDS_QUERY_PARAM, query);
            if (targetId) next.set(RECORDS_TARGET_PARAM, targetId);
          }
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // An old link names the list by resource type (`records=ForumAccess`); the address is swapped for
  // the kind's slug, which is what a link to the list carries from now on.
  const linkedByType = kinds.find((kind) => kind.perRecord && kind.resourceType === openRecords);
  useEffect(() => {
    if (linkedByType) setRecordsState(linkedByType.slug, recordsPage, recordsQuery, true);
  }, [linkedByType, recordsPage, recordsQuery, setRecordsState]);
  const sections = useMemo(() => buildPersonAreas(context), [context]);
  const shown = useMemo(
    () => filterPersonAreas(sections, context, filter, text, changes.removedJustNow),
    [sections, context, filter, text, changes.removedJustNow],
  );
  const summary = personSummary(sections, kinds, personGrants.permissionCounts);
  const isSigner = holdsSigningAuthority(context);
  const signingKind = signingAuthorityKind(kinds);

  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const grantorIds = useMemo(() => new Set(grants.flatMap(grantPeopleIds)), [grants]);
  const grantors = usePersonNames(grantorIds);

  const held = useMemo(() => {
    const map = new Map<string, Permission>();
    for (const section of sections) {
      for (const row of section.granted) {
        if (row.permission && !row.permission.isDisabled && !map.has(row.kind.slug)) map.set(row.kind.slug, row.permission);
      }
    }
    return map;
  }, [sections]);

  const modelOf = (row: PersonKindRow): GrantRowModel =>
    buildGrantRow({
      kind: row.kind,
      kinds,
      permission: row.permission,
      viewerUserId,
      mayAdministerPlatform,
      // The template page refuses anybody without administration access, a member on their own page included.
      mayOpenTemplates: sessionUser?.canAccessAdministration() ?? false,
      grantor: row.permission ? personNameState(grantors, row.permission.grantorUserId.value) : undefined,
      remover: row.permission ? removerNameState(grantors, row.permission) : undefined,
    });

  // The header's buttons are far from the row they change, so a grant made there is also told in a
  // snackbar; a removal tells itself, with its Undo.
  const makeSigner = async () => {
    if (!signingKind) return;
    if (await changes.grant(signingKind)) {
      enqueueSnackbar(changeAnnouncements.granted(signingKind.name, personName), { variant: "success", role: "status" });
    }
  };

  const removeSigningAuthority = async () => {
    if (!signingKind) return;
    for (const grant of liveSigningAuthorityGrants(context)) await changes.remove(grant, signingKind);
  };

  const openApplyTemplate = (template: PermissionTemplate) =>
    openDialog({
      resetStack: true,
      component: <ApplyTemplateDialog granteeUserIds={[person.userId]} template={template} />,
      title: "Apply Permission Template",
      MuiProps: { maxWidth: "md" },
    });

  const exporting = useGrantExport({
    scope: { subject: "person", userId: person.userId, states: EXPORT_STATES[filter] },
    kinds,
    subject: knownName ?? person.userId.value,
  });

  // Remove all access asks for the person's name to be typed; a person the directory did not name is
  // confirmed with a fixed phrase, so the action, and its "Also stop sign-in", stay available.
  const menuActions: RowAction[] = [
    // In the menu beside Remove all access: three buttons already share the header's line, and a
    // fourth squeezed the summary into a column at laptop width and wrapped the menu alone on a phone.
    {
      key: "export",
      label: exporting.label,
      icon: <FileDownloadIcon />,
      disabled: exporting.busy,
      onClick: exporting.start,
    },
    {
      key: "remove-all",
      label: removeAllAccess.menuItem,
      ariaLabel: `Remove all access for ${personName}`,
      icon: <PersonOffIcon />,
      destructive: true,
      disabled: changes.pending.has(REMOVE_ALL_KEY),
      onClick: (anchor) => {
        removeAllAnchor.current = anchor;
        setRemoveAllOpen(true);
      },
    },
  ];

  // Afterwards the page shows what went, under Removed; Undo brings back the whole view.
  const removeAll = async (stopSignIn: boolean) => {
    const removed = await changes.removeAll(stopSignIn, {
      returnFocus: () => removeAllAnchor.current?.focus(),
      onRestored: () => setFilter("all"),
    });
    if (removed) setFilter("removed");
    return removed;
  };

  /**
   * A grant on one record made from Add access lands in that kind's list, and the filters that could
   * hide it are cleared. A new grant is the newest, so the list opens on its first page. A change to a
   * grant the person held keeps that grant's created date, so it may sit on any page of a list ordered
   * newest first: the list opens narrowed to the changed record instead (#6837). The count comes with
   * the refetch the change triggers, and the row with the change highlight.
   */
  const afterRecordChange = async (kind: PermissionKind, change: Promise<boolean>, changed?: Permission) => {
    const done = await change;
    if (!done || !kind.perRecord) return done;
    setFilter("all");
    setText("");
    setRecordsState(kind.slug, 0, "", false, changed ? targetIdOf(kind, changed) : undefined);
    return done;
  };

  const signerPending = signingKind ? changes.pending.has(`kind-${signingKind.slug}`) : false;
  // Export is a read, so a reader with Read-Only, and a member on their own page, have it too: as the
  // header's one button, since they have no menu.
  const actions = readOnly ? (
    <GrantExportButton action={exporting} />
  ) : (
    <>
      {signingKind &&
        (isSigner ? (
          <Button variant="contained" startIcon={<EditOffIcon />} disabled={signerPending} onClick={() => void removeSigningAuthority()}>
            Remove signing authority
          </Button>
        ) : (
          <Button variant="contained" startIcon={<DrawIcon />} disabled={signerPending} onClick={() => void makeSigner()}>
            Make a signer
          </Button>
        ))}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
        Add access
      </Button>
      <ApplyTemplateMenuButton variant="outlined" label="Apply template" onApply={openApplyTemplate} />
      {/* The menu closes as Export is pressed, so how far it has read is said beside it. */}
      {exporting.busy && (
        <Typography variant="body2" color="text.secondary" role="status">
          {exporting.label}
        </Typography>
      )}
      <RowActions actions={menuActions} collapsed ariaLabel={`More actions for ${personName}`} />
    </>
  );

  const heading = personTitle(person);
  const secondary = [person.email, organization ? `Permissions in ${organization}` : undefined].filter(Boolean).join(" · ");

  const allRows = sections.flatMap((section) => [...section.granted, ...section.notGranted]);
  const selectedRow = typeof selected === "string" ? allRows.find((row) => row.key === selected) : undefined;
  const selectedRecord = typeof selected === "object" ? selected : undefined;
  const details: GrantDetailsSelection | undefined = selectedRow
    ? { row: modelOf(selectedRow), permission: selectedRow.permission }
    : selectedRecord;

  const renderRow = (row: PersonKindRow) => {
    const permission = row.permission;
    const key = permission?.id?.value ?? `kind-${row.kind.slug}`;
    const removedHere = changes.removedJustNow.has(key) && permission?.isDisabled;
    const model = removedHere ? { ...modelOf(row), removedText: REMOVED_JUST_NOW } : modelOf(row);
    const rowActions: RowAction[] = isExtraLarge
      ? [
          {
            key: "details",
            label: "Show details",
            ariaLabel: `Show details of ${model.kind.name} for ${personName}`,
            icon: <InfoOutlinedIcon />,
            onClick: () => setSelected(row.key),
          },
        ]
      : [];
    return (
      <GrantRow
        key={row.key}
        row={model}
        subject="person"
        personName={personName}
        pending={changes.pending.has(key)}
        alert={changes.alerts.get(key)}
        highlight={permission?.id ? changes.highlighted.has(permission.id.value) : false}
        onSwitch={
          readOnly || !permission
            ? undefined
            : (enabled) => void (enabled ? changes.grantAgain(permission, row.kind) : changes.remove(permission, row.kind))
        }
        onChangeLevel={readOnly || !permission ? undefined : (level) => void changes.changeLevel(permission, row.kind, level)}
        onGrant={readOnly ? undefined : () => void changes.grant(row.kind)}
        onRetryGrantor={grantors.retry}
        actions={rowActions}
        kindNote={row.alsoGranted ? ALSO_GRANTED : undefined}
        reach={withInvitationAnswer(row.kind, reachOf(model, permission))}
      />
    );
  };

  let body: ReactNode;
  if (personGrants.error) {
    body = (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={personGrants.retry}>
            Retry
          </Button>
        }
      >
        {`Unable to load ${personName}'s permissions. ${personGrants.error}`}
      </Alert>
    );
  } else if (personGrants.isLoading) {
    body = <AreaSkeletons />;
  } else if (shown.length === 0) {
    body = (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No permission matches these filters.
      </Typography>
    );
  } else {
    body = (
      <Box sx={{ display: "grid", gap: 2 }}>
        {shown.map((section) => (
          <PersonAreaSection
            key={section.area}
            section={section}
            counts={areaCounts(section, filter)}
            expandNotGranted={filter === "not-granted"}
            renderRow={renderRow}
            renderRecords={({ kind, count }) => {
              const open = openRecords === kind.slug || openRecords === kind.resourceType;
              return (
                <PersonRecordList
                  kind={kind}
                  count={count}
                  mode={filter === "removed" ? "removed" : "live"}
                  kinds={kinds}
                  person={{ userId: person.userId, name: personName }}
                  viewerUserId={viewerUserId}
                  mayAdministerPlatform={mayAdministerPlatform}
                  readOnly={readOnly}
                  searchScope={sessionUser?.canReadMemberPermissions() ? "organization" : "readable"}
                  changes={changes}
                  open={open}
                  pageIndex={open ? recordsPage : 0}
                  query={open ? recordsQuery : ""}
                  targetId={open ? recordsTarget : undefined}
                  onToggle={() => setRecordsState(open ? undefined : kind.slug, 0, "", false)}
                  onPage={(pageIndex) => setRecordsState(kind.slug, pageIndex, recordsQuery, false, recordsTarget)}
                  onShowAll={() => setRecordsState(kind.slug, 0, "", false)}
                  onQuery={(query) => setRecordsState(kind.slug, 0, query, true)}
                  onShowDetails={isExtraLarge ? setSelected : undefined}
                  selectedDetails={selectedRecord}
                />
              );
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Page sx={{ "--person-header-height": `${headerHeight}px` }}>
      <Layout>
        {isLarge && (
          <SideColumn>
            <AreaNav areas={sections.map((section) => section.area)} />
          </SideColumn>
        )}
        <Box sx={{ minWidth: 0 }}>
          <StickyHeader ref={setHeaderElement}>
            <PermissionsSubjectHeader
              trail={[{ label: "Permissions", to: "/permissions" }]}
              title={heading.title}
              titleLoading={heading.loading}
              titleAction={heading.action}
              avatar={person.avatar}
              secondary={secondary}
              role={memberRole(isSigner)}
              actions={actions}
            >
              <Typography variant="body2" color="text.secondary">
                {personGrants.isLoading ? <Skeleton width="40%" /> : summary.join(" · ")}
              </Typography>
            </PermissionsSubjectHeader>
            {isOwnPage && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                {OWN_PAGE_NOTICE}
              </Alert>
            )}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1.5, alignItems: "center" }}>
              <TextField
                size="small"
                label={knownName ? `Filter ${knownName}'s grants` : "Filter grants"}
                value={text}
                onChange={(event) => setText(event.target.value)}
                sx={{ flex: "1 1 16rem" }}
                slotProps={{
                  // The label names the person (#6330).
                  inputLabel: { className: SESSION_RECORDING_MASK_CLASS },
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filter}
                aria-label="Show"
                onChange={(_, value: PersonGrantFilter | null) => {
                  if (value) setFilter(value);
                }}
                sx={[
                  {
                    flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    "& .MuiToggleButton-root": { flex: 1, minHeight: 40, textTransform: "none", whiteSpace: "nowrap" },
                  },
                  touchTargetSx("& .MuiToggleButton-root"),
                ]}
              >
                {PERSON_GRANT_FILTERS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </StickyHeader>
          {body}
        </Box>
        {isExtraLarge && (
          <SideColumn>
            <PersonGrantDetails selection={details} viewerUserId={viewerUserId} />
          </SideColumn>
        )}
      </Layout>
      {!readOnly && (
        <AddAccessDialog
          open={addOpen}
          userId={person.userId}
          personName={personName}
          areas={areas}
          kinds={kinds}
          held={held}
          mayAdministerPlatform={mayAdministerPlatform}
          onClose={() => setAddOpen(false)}
          onGrant={(kind, level, target) => afterRecordChange(kind, changes.grant(kind, level, target))}
          onChangeLevel={(permission, kind, level) => afterRecordChange(kind, changes.changeLevel(permission, kind, level), permission)}
          onChangeFields={(permission, kind, fields) =>
            afterRecordChange(kind, changes.changeFields(permission, kind, fields), permission)
          }
        />
      )}
      {!readOnly && (
        <RemoveAllAccessDialog
          open={removeAllOpen}
          personName={personName}
          named={Boolean(knownName)}
          organization={organization}
          review={removeAllAccessReview(context, personGrants.permissionCounts)}
          onClose={() => setRemoveAllOpen(false)}
          onConfirm={removeAll}
        />
      )}
      {/* One polite region for every change this page makes, in the wording module's words. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        <span key={changes.announcement.key}>{changes.announcement.text}</span>
      </Box>
    </Page>
  );
}

/**
 * The route: resolves `me`, gates, and finds the person. A colleague's page needs Member Permissions;
 * your own needs only membership. The kinds are what every row is named from, so the page waits for
 * them, and a failure to read them is the page's one error.
 */
export default function PersonPermissionsPage() {
  const { userId: segment } = useParams();
  const sessionUser = useSessionUser();
  const { photo } = useOwnIndividual();
  const kindsQuery = useGetPermissionKindsQuery();

  const viewerId = sessionUser?.id;
  // Compared as ids, not strings: the segment may be in any case, and the page must not offer
  // somebody their own permissions to edit because they upper-cased their id in the address bar.
  const isOwnPage =
    segment === OWN_PERMISSIONS_SEGMENT || (segment !== undefined && Guid.isValid(segment) && new Guid(segment).isEqualTo(viewerId));
  const userId = isOwnPage ? viewerId : segment && Guid.isValid(segment) ? new Guid(segment) : undefined;
  const mayRead = isOwnPage || (sessionUser?.canReadMemberPermissions() ?? false);

  // Whether the directory named the person: found, `missing` (no longer a member), `unauthorized` (a
  // person the viewer may not see), a lookup that failed, or one still under way. Memoized on purpose:
  // a fresh set each render would be a fresh lookup argument.
  const lookedUp = !isOwnPage && mayRead ? userId?.value : undefined;
  const lookupIds = useMemo(() => new Set(lookedUp ? [lookedUp] : []), [lookedUp]);
  const directory = usePersonNames(lookupIds);
  const individual = lookedUp ? directory.individuals.get(lookedUp.toLowerCase()) : undefined;

  if (!sessionUser) return null;
  if (!mayRead) return <NotAuthorized />;
  if (!userId) return <NotAuthorized message="There is no person at this address." />;

  if (kindsQuery.error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => void kindsQuery.refetch()}>
            Retry
          </Button>
        }
      >
        Unable to load the kinds of permission. {getErrorMessageFromUnknown(kindsQuery.error)}
      </Alert>
    );
  }
  if (!kindsQuery.data) return <AreaSkeletons />;

  const person: Person = isOwnPage
    ? {
        userId,
        name: sessionUser.fullName,
        email: sessionUser.email?.value,
        avatar: (
          <Avatar alt="" src={photo}>
            {sessionUser.initials}
          </Avatar>
        ),
        state: { state: "found", name: sessionUser.fullName },
      }
    : {
        userId,
        name: individual?.fullName,
        email: individual?.email?.value,
        avatar: <IndividualAvatar individual={individual} size="medium" />,
        state: personNameState(directory, userId.value),
        retry: directory.retry,
      };

  return (
    <PersonPermissions
      key={userId.value}
      person={person}
      isOwnPage={isOwnPage}
      kinds={kindsQuery.data.kinds}
      areas={kindsQuery.data.areas}
    />
  );
}

